import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import isEqual from 'deep-equal';
import { BackendSubscriptionAction } from '../../../request/interfaces/backend-subscription';
import { RetryApiAlert } from '../../../shared/components/RetryApiAlert/RetryApiAlert';
import { JobsAPI } from '../../JobsAPI';
import { jobDetailsDataIds } from './data-ids';
import { useQuery } from '../../../request/hooks/useQuery';
import { EntityUIState } from '../../../request/interfaces/entity-ui-state';
import { JobStatsBox } from './JobStats/JobStatsBox/JobStatsBox';
import { GenericPageShimmer } from '../../../shimmer/GenericPageShimmer/GenericPageShimmer';
import { getErrorMessageFromAxiosError } from '../../../request/utils';
import { JobObjectList } from './ObjectList/JobObjectList';
import { ListChanges, ListMetaData } from '../../../list-conductor/list-meta-data';
import { OffsetPaginationData } from '../../../list-conductor/offset-pagination-strategy';
import { JobObjectListGlobalContext } from './ObjectList/interface';
import { JobFailuresFactory } from '../../factory';
import { JobType } from '../../interfaces/job';

export function JobDetailsPage() {
  const { id, jobId = '' } = useParams();
  const pipelineId = parseInt(id as string, 10);

  const [defaultListMeta, setDefaultListMeta] = useState<
    ListMetaData<OffsetPaginationData> | undefined
  >();

  const [rowsExpandedState, setRowsExpandedState] = useState<
    JobObjectListGlobalContext['rowsExpandedState']
  >({});

  const {
    data,
    uiState,
    error,
    fetch: getJobsDetails
  } = useQuery({
    dataSource$: () => {
      return Promise.all([JobsAPI.getJobDetails(jobId), JobsAPI.getJobFailures(jobId)]);
    },
    subscriptionQueries: [
      {
        event: 'PIPELINE_STATUS_UPDATED',
        ids: [pipelineId],
        action: BackendSubscriptionAction.UPDATE
      }
    ]
  });

  const jobDetails = data ? data[0] : undefined;
  const jobFailures = data ? JobFailuresFactory(data[1]) : [];

  useEffect(() => {
    getJobsDetails();
  }, [jobId]);

  const onListMetaChange = useCallback((changes: ListChanges<OffsetPaginationData> | undefined) => {
    if (changes?.previousValue && !isEqual(changes?.currentValue, changes?.previousValue)) {
      setRowsExpandedState({});
    }
    setDefaultListMeta(changes?.currentValue);
  }, []);

  const latencyNotApplicable = [JobType.HISTORICAL, JobType.TRUNCATE_AND_LOAD].includes(
    jobDetails?.mode as JobType
  );

  return (
    <>
      {uiState === EntityUIState.LOADING ? (
        <GenericPageShimmer className='m-auto' dataId={jobDetailsDataIds.loader} />
      ) : null}

      {jobDetails && [EntityUIState.IDLE, EntityUIState.REFRESHING].includes(uiState) ? (
        <div className='h-100 overflow-auto'>
          <div className='body-container'>
            <JobStatsBox
              pipelineId={parseInt(id as string, 10)}
              jobDetails={jobDetails}
              latencyNotApplicable={latencyNotApplicable}
              dataId={jobDetailsDataIds.statsBox}
              onRefreshClick={getJobsDetails}
              jobFailures={jobFailures}
            />

            <JobObjectList
              jobId={jobId}
              pipelineId={pipelineId}
              dataId={jobDetailsDataIds.objectList}
              defaultListMeta={defaultListMeta}
              isLoadOnly={jobDetails.isLoadOnly}
              latencyNotApplicable={latencyNotApplicable}
              onListMetaChange={onListMetaChange}
              tableComponentProps={{
                rowsExpandedState,
                setRowsExpandedState
              }}
            />
          </div>
        </div>
      ) : null}

      {uiState === EntityUIState.ERRORED && error ? (
        <RetryApiAlert
          dataId={jobDetailsDataIds.retryAlert}
          className='m-auto'
          title='Failed to load the Job details'
          subTitle={getErrorMessageFromAxiosError(error) || 'Retry loading the Job details'}
          secondaryCTAProps={{
            onClick: getJobsDetails
          }}
        />
      ) : null}
    </>
  );
}
